import { useEffect, useRef, useState, useMemo } from 'react';
import { fmt, STATUS_TONE } from '../utils/format.js';

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

export function Button({
  children, variant = 'primary', size = 'md', loading = false,
  icon, type = 'button', className = '', ...rest
}) {
  return (
    <button
      type={type}
      className={`btn btn--${variant} btn--${size} ${loading ? 'is-loading' : ''} ${className}`}
      disabled={loading || rest.disabled}
      {...rest}
    >
      {loading && <span className="spinner spinner--sm" aria-hidden="true" />}
      {!loading && icon && <span className="btn__icon" aria-hidden="true">{icon}</span>}
      {children}
    </button>
  );
}

export function Badge({ children, tone, status }) {
  // A status maps to its house colour so the same state never changes shade.
  const resolved = tone ?? STATUS_TONE[status] ?? 'neutral';
  return <span className={`badge badge--${resolved}`}>{children ?? fmt.label(status)}</span>;
}

export function Card({ title, subtitle, actions, children, className = '', padded = true }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card__header">
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {subtitle && <p className="card__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="card__actions">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'card__body' : ''}>{children}</div>
    </section>
  );
}

export function StatTile({ label, value, hint, tone = 'default', icon, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      className={`stat stat--${tone} ${onClick ? 'stat--clickable' : ''}`}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      {icon && <span className="stat__icon" aria-hidden="true">{icon}</span>}
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      {hint && <span className="stat__hint">{hint}</span>}
    </Tag>
  );
}

export function Avatar({ name, src, size = 36 }) {
  const [failed, setFailed] = useState(false);

  if (src && !failed) {
    return (
      <img
        className="avatar"
        src={src}
        alt={name ?? ''}
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className="avatar avatar--initials"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-label={name}
    >
      {fmt.initials(name)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* States                                                                      */
/* -------------------------------------------------------------------------- */

export function Spinner({ label = 'Loading…' }) {
  return (
    <div className="state state--loading">
      <span className="spinner" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}

export function EmptyState({ icon = '📭', title, message, action }) {
  return (
    <div className="state state--empty">
      <span className="state__icon" aria-hidden="true">{icon}</span>
      <h3>{title}</h3>
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  const isPermission = error?.status === 403;

  return (
    <div className="state state--error">
      <span className="state__icon" aria-hidden="true">{isPermission ? '🔒' : '⚠️'}</span>
      <h3>{isPermission ? 'You do not have access to this' : 'Something went wrong'}</h3>
      <p>{error?.message ?? 'Please try again.'}</p>
      {onRetry && !isPermission && <Button variant="secondary" onClick={onRetry}>Try again</Button>}
    </div>
  );
}

/** The standard loading → error → empty → content ladder for a screen body. */
export function AsyncContent({ loading, error, onRetry, isEmpty, empty, children }) {
  if (loading) return <Spinner />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (isEmpty) return empty ?? <EmptyState title="Nothing here yet" />;
  return children;
}

/* -------------------------------------------------------------------------- */
/* Form fields                                                                 */
/* -------------------------------------------------------------------------- */

export function Field({ label, error, hint, required, children, className = '' }) {
  return (
    <label className={`field ${error ? 'field--error' : ''} ${className}`}>
      {label && (
        <span className="field__label">
          {label}
          {required && <span className="field__required" aria-hidden="true"> *</span>}
        </span>
      )}
      {children}
      {error && <span className="field__error">{error}</span>}
      {!error && hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Input({ error, ...rest }) {
  return <input className={`input ${error ? 'input--error' : ''}`} {...rest} />;
}

export function Select({ options = [], placeholder, error, ...rest }) {
  return (
    <select className={`input ${error ? 'input--error' : ''}`} {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => {
        const value = opt.value ?? opt._id ?? opt.id;
        const label = opt.label ?? opt.name ?? opt.title ?? opt.code;
        return (
          <option key={value} value={value}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

export function Textarea({ error, rows = 3, ...rest }) {
  return <textarea className={`input ${error ? 'input--error' : ''}`} rows={rows} {...rest} />;
}

export function Checkbox({ label, hint, ...rest }) {
  return (
    <label className="checkbox">
      <input type="checkbox" {...rest} />
      <span>
        {label}
        {hint && <small className="checkbox__hint">{hint}</small>}
      </span>
    </label>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="search">
      <span className="search__icon" aria-hidden="true">🔍</span>
      <input
        className="input search__input"
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Modal                                                                       */
/* -------------------------------------------------------------------------- */

export function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  const dialogRef = useRef(null);

  /**
   * Held in a ref so the effect below does not depend on this callback.
   *
   * Callers pass an inline arrow, which is a different function on every
   * render. With `onClose` in the dependency list the effect re-ran on every
   * render - including the one caused by each keystroke - and re-focused the
   * dialog, so typing into any field inside a modal lost the caret after a
   * single character.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Escape closes, the page behind must not scroll, and the dialog takes
  // focus once: when it opens.
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    dialogRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={dialogRef}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h2>{title}</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', tone = 'danger', pending }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button variant={tone} onClick={onConfirm} loading={pending}>{confirmLabel}</Button>
        </>
      }
    >
      <p className="confirm__message">{message}</p>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Table                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Columns: `{ key, header, render?, align?, width? }`.
 * `render(row)` wins over reading `row[key]`.
 */
export function DataTable({ columns, rows, rowKey = (r) => r._id ?? r.id, onRowClick, emptyMessage = 'No records found' }) {
  if (!rows?.length) {
    return <div className="table-empty">{emptyMessage}</div>;
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={{ textAlign: col.align ?? 'left', width: col.width }}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'table__row--clickable' : ''}
            >
              {columns.map((col) => (
                <td key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                  {col.render ? col.render(row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({ meta, page, onChange }) {
  if (!meta || meta.totalPages <= 1) return null;

  const { totalPages, total, limit } = meta;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Window the page numbers so 200 pages do not render 200 buttons.
  const windowed = useMemo(() => {
    const span = 2;
    const pages = new Set([1, totalPages]);
    for (let i = page - span; i <= page + span; i += 1) {
      if (i > 0 && i <= totalPages) pages.add(i);
    }
    return [...pages].sort((a, b) => a - b);
  }, [page, totalPages]);

  return (
    <nav className="pagination" aria-label="Pagination">
      <span className="pagination__info">
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>

      <div className="pagination__controls">
        <button type="button" onClick={() => onChange(page - 1)} disabled={page <= 1}>‹</button>

        {windowed.map((p, i) => (
          <span key={p} style={{ display: 'contents' }}>
            {i > 0 && windowed[i - 1] !== p - 1 && <span className="pagination__gap">…</span>}
            <button
              type="button"
              className={p === page ? 'is-current' : ''}
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          </span>
        ))}

        <button type="button" onClick={() => onChange(page + 1)} disabled={page >= totalPages}>›</button>
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout helpers                                                              */
/* -------------------------------------------------------------------------- */

export function PageHeader({ title, subtitle, actions, breadcrumb }) {
  return (
    <header className="page-header">
      <div>
        {breadcrumb && <nav className="breadcrumb">{breadcrumb}</nav>}
        <h1>{title}</h1>
        {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function Toolbar({ children }) {
  return <div className="toolbar">{children}</div>;
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          className={`tabs__tab ${active === tab.key ? 'is-active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.count != null && <span className="tabs__count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Definition list used across the detail screens. */
export function DetailGrid({ items, columns = 2 }) {
  return (
    <dl className="detail-grid" style={{ '--cols': columns }}>
      {items.filter(Boolean).map(({ label, value, full }) => (
        <div key={label} className={full ? 'detail-grid__item detail-grid__item--full' : 'detail-grid__item'}>
          <dt>{label}</dt>
          <dd>{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Horizontal bar for "12 of 30 days used" style figures. */
export function ProgressBar({ value, max, tone = 'primary', label }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="progress" title={label ?? `${value} of ${max}`}>
      <div className={`progress__fill progress__fill--${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}
